import { useState } from 'react';
import { api, enc } from '../api';
import { fmtWhen } from '../format';
import { useLoad } from '../state';
import { useFeedback } from '../ui/feedback';
import { Choice } from '../ui/fields';
import { Empty, Loading, Sub } from '../ui/bits';
import { type Item } from './Queue';

// Andrew's strip, generalised: one box per playbook stage with counts, the current blocker and who it waits on.
// The strip is a view over work items; the ladder still decides when the campaign is live.
export function WorkforceStrip({ mid, canEdit }: { mid: string; canEdit: boolean }) {
  const { toast } = useFeedback();
  const [picked, setPicked] = useState<string | null>(null);
  const { data, error, reload } = useLoad(() => api('GET', `/campaigns/${enc(mid)}/workforce`), [mid]);
  const items = useLoad(() => picked ? api('GET', `/work?campaign=${enc(mid)}&stage=${enc(picked)}&status=all`) : Promise.resolve(null), [mid, picked]);
  if (error) return <section className="strip-wrap"><Empty>{error}</Empty></section>;
  if (!data) return <section className="strip-wrap"><Loading /></section>;
  const { playbook, stages, current_stage_id, choices } = data as { playbook: any; stages: any[]; current_stage_id: string | null; choices: any[] };
  if (!playbook) {
    if (!canEdit) return null;
    const start = async (ev: React.FormEvent<HTMLFormElement>) => {
      ev.preventDefault();
      const code = String(new FormData(ev.currentTarget).get('playbook_code') || '');
      if (!code) return;
      try { const r = await api('POST', `/campaigns/${enc(mid)}/workforce/start`, { playbook_code: code }); toast(r.first_item?.created ? 'Playbook started; the first stage is queued' : 'Playbook already started'); reload(); }
      catch (e) { toast((e as Error).message, true); }
    };
    return (
      <section className="strip-wrap">
        <form className="inline-form" onSubmit={start}>
          <div className="field"><label>Agent workforce</label>
            <Choice name="playbook_code" options={choices.map((c) => ({ value: c.code, label: `${c.workforce === 'sales' ? 'Sales' : 'Marketing'} · ${c.name}` }))} blank="No playbook yet: choose one to put agents to work on this campaign" />
          </div>
          <button type="submit">Start</button>
        </form>
      </section>
    );
  }
  return (
    <section className="strip-wrap">
      <h2 className="strip-title"><span>{playbook.workforce} agent</span> workforce <Sub>{playbook.name}</Sub></h2>
      <ol className="strip">
        {stages.map((s) => (
          <li key={s.id} className={[s.id === current_stage_id ? 'current' : '', s.open ? 'open' : s.done ? 'done' : 'idle', s.overdue ? 'late' : '', picked === s.id ? 'picked' : ''].filter(Boolean).join(' ')}>
            <button type="button" onClick={() => setPicked(picked === s.id ? null : s.id)}>
              <span className="box">{s.name}</span>
              <span className="meta"><span className={`actor actor-${s.actor}`}>{s.actor}</span>{s.open ? `${s.open} open` : s.done ? `${s.done} done` : 'not started'}{s.in_review ? ` · ${s.in_review} to review` : ''}{s.blocked ? ` · ${s.blocked} blocked` : ''}</span>
              {s.blocker && <span className={`blocker ${s.blocker.bucket === 'overdue' ? 'overdue' : ''}`}>{s.blocker.assignee_name || 'unassigned'}{s.blocker.due_at ? ` · ${fmtWhen(s.blocker.due_at)}` : ''}</span>}
            </button>
          </li>
        ))}
      </ol>
      {picked && (
        <div className="strip-items">
          {items.loading ? <Loading /> : (items.data?.items ?? []).length ? (
            <table><tbody>{(items.data.items as Item[]).map((i) => (
              <tr key={i.id} className={['done', 'skipped', 'expired'].includes(i.status) ? 'muted' : ''}><td className="title-col"><a href={`#/work/${i.id}`}>{i.title}</a></td><td className="muted">{i.assignee_name || '—'}</td><td className="muted">{i.status.replace(/_/g, ' ')}</td><td className="nowrap muted">{i.due_at ? fmtWhen(i.due_at) : ''}</td></tr>
            ))}</tbody></table>
          ) : <Empty>No work items for this stage yet.</Empty>}
        </div>
      )}
    </section>
  );
}
